import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Compass,
  Info,
  Star,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { getVanKhiLichNgay, type VanKhiBreakdown, type VanKhiLichNgay } from "@/lib/boide-api";
import { cn } from "@/lib/utils";

import { VanKhiLuckyInline } from "./van-khi-lucky";
import { AlmanacChip, AlmanacGroup, AlmanacRow, ChipList } from "./van-khi-row";
import { VanKhiTietKhiInline } from "./van-khi-tiet-khi";

const WEEKDAY = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];

function parts(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return { y, m, d, wd };
}
function shift(iso: string, n: number) {
  const { y, m, d } = parts(iso);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Màu theo điểm — cùng ngưỡng với xếp loại ở BE (80/65/55/45/30). */
function tone(score: number) {
  if (score >= 65)
    return { text: "text-element-wood", bar: "bg-element-wood", stroke: "stroke-element-wood" };
  if (score >= 55) return { text: "text-primary", bar: "bg-primary", stroke: "stroke-primary" };
  if (score >= 45)
    return { text: "text-element-earth", bar: "bg-element-earth", stroke: "stroke-element-earth" };
  return { text: "text-destructive", bar: "bg-destructive/80", stroke: "stroke-destructive" };
}

function Gauge({
  score,
  bucket,
  children,
}: {
  score: number;
  bucket: string;
  children?: ReactNode;
}) {
  // Nửa vòng tròn 180°, bán kính 40 → chu vi nửa = π·40.
  const half = Math.PI * 40;
  const t = tone(score);
  return (
    <div className="almanac-gauge">
      <div
        className="absolute inset-0"
        role="img"
        aria-label={`Điểm vận khí ${score} trên 100, ${bucket}`}
      >
        <svg viewBox="0 0 100 56" className="size-full" aria-hidden="true">
          <path
            d="M10 50 A40 40 0 0 1 90 50"
            className="fill-none stroke-muted/50"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <path
            d="M10 50 A40 40 0 0 1 90 50"
            className={cn("fill-none transition-all duration-700", t.stroke)}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${(Math.max(0, Math.min(100, score)) / 100) * half} ${half}`}
          />
        </svg>
        <div className="absolute inset-x-0 bottom-0 text-center">
          <p className={cn("almanac-score", t.text)}>
            {score}
            <span className="text-xs text-muted-foreground">/100</span>
          </p>
          <p className="mt-0.5 text-[11px] font-semibold text-muted-foreground">{bucket}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

const GROUP_LABEL: Record<VanKhiBreakdown["group"], string> = {
  nen: "Nền",
  ngay: "Theo ngày",
  tuoi: "Theo tuổi của bạn",
  viec: "Theo việc",
};

/**
 * Bảng "Lịch ngày": mọi thông số của một ngày, có căn cứ. `date` do trang cha giữ
 * (đồng bộ với dải chọn ngày); bấm cột trong biểu đồ 7 ngày gọi `onSelectDate`.
 */
/**
 * Mọi thông tin của ngày nay gom vào MỘT tờ lịch (việc nên/không nên, giờ, hướng, màu
 * số, tiết khí, cát/hung tinh). `part="details"` không còn gì để hiện — giữ tham số để
 * nơi gọi cũ không vỡ.
 */
export function VanKhiLichNgayPanel({
  date,
  onSelectDate,
  part,
}: {
  date?: string;
  onSelectDate?: (iso: string) => void;
  part?: "summary" | "details";
}) {
  const [own, setOwn] = useState<string | undefined>(undefined);
  const active = date ?? own;
  const select = (iso: string) => (onSelectDate ? onSelectDate(iso) : setOwn(iso));
  const query = useQuery({
    queryKey: ["van-khi-lich-ngay", active ?? "today"],
    queryFn: () => getVanKhiLichNgay(active),
    staleTime: 10 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  if (query.isLoading)
    return <Skeleton className={cn("w-full rounded-3xl", part === "summary" ? "h-64" : "h-96")} />;
  if (!query.data) return null;
  const data = query.data;
  const showSummary = part !== "details";
  return (
    <div className={cn("space-y-4 transition-opacity", query.isFetching && "opacity-70")}>
      {showSummary ? <Header data={data} onShift={(n) => select(shift(data.date, n))} /> : null}
    </div>
  );
}

function Header({ data, onShift }: { data: VanKhiLichNgay; onShift: (n: number) => void }) {
  const p = parts(data.date);
  return (
    <section className="almanac-panel almanac-calendar" aria-label={`Lịch ngày ${data.date}`}>
      <div className="almanac-calendar-body">
        {/* Cột ngày: thứ ngày ngay trên số, mũi tên lật ngày hai bên số — mọi thứ chung
            một trục giữa của cột. */}
        <div className="almanac-calendar-date">
          <p className="almanac-calendar-weekday">
            {WEEKDAY[p.wd]} · {p.m}/{p.y}
            {data.date === data.today ? (
              <span className="almanac-today-dot" aria-label="Hôm nay" />
            ) : null}
          </p>
          <div className="almanac-date-row">
            <button
              type="button"
              aria-label="Ngày trước"
              onClick={() => onShift(-1)}
              className="almanac-arrow"
            >
              <ChevronLeft className="size-4" />
            </button>
            <p className="almanac-date-number">{p.d}</p>
            <button
              type="button"
              aria-label="Ngày sau"
              onClick={() => onShift(1)}
              className="almanac-arrow"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
          <p className="text-xs leading-5 text-foreground">
            {data.lunar.day} tháng {data.lunar.month} âm lịch
          </p>
          <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
            {data.can_chi.ngay} · {data.can_chi.thang}
          </p>
        </div>
        <div className="min-w-0 text-center">
          <Gauge score={data.score} bucket={data.bucket}>
            <ScoreInfo data={data} />
          </Gauge>
          {data.gio_hien_tai ? (
            <p className="mt-3 text-[11px] text-muted-foreground">Bây giờ: {data.gio_hien_tai}</p>
          ) : null}
        </div>
      </div>
      <div className="almanac-calendar-meta">
        <Mini label="Trực" value={data.truc.name} />
        <Mini label={data.sao.is_hoang_dao ? "Hoàng đạo" : "Hắc đạo"} value={data.sao.name} />
        <Mini
          label="Nhị thập bát tú"
          value={`${data.nhi_thap_bat_tu.name} · ${data.nhi_thap_bat_tu.tot ? "tốt" : "xấu"}`}
        />
      </div>
      {/* Thứ tự theo độ cần: làm gì → giờ / hướng → màu số tuổi → tiết khí → tra sao. */}
      <Thien data={data} />
      <TimeAndDirection data={data} />
      <VanKhiLuckyInline mayMan={data.may_man} tuoiHop={data.tuoi_hop} />
      {data.tiet_khi_info ? <VanKhiTietKhiInline tk={data.tiet_khi_info} /> : null}
      <NgocHap data={data} />
      <More data={data} />
      {/* BE cũ chưa có câu chốt thì vẫn cần dòng cảnh báo ngày kỵ. */}
      {data.ngay_ky.length && !data.viec_tom_tat ? (
        <p className="mt-3 rounded-xl bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
          Phạm {data.ngay_ky.map((k) => k.name).join(", ")} — kiêng việc trọng đại.
        </p>
      ) : null}
      {!data.personalized ? (
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          Đăng nhập và thêm ngày sinh để điểm tính theo tuổi của bạn.
        </p>
      ) : null}
    </section>
  );
}

/** Giờ tốt + hướng đi. Căn cứ của từng giờ / độ la bàn của từng hướng nằm ở tooltip. */
function TimeAndDirection({ data }: { data: VanKhiLichNgay }) {
  const gio = data.gio_tot.slice(0, 4);
  if (!gio.length && !data.huong.length) return null;
  return (
    <AlmanacGroup>
      {gio.length ? (
        <AlmanacRow icon={<Clock />} label="Giờ tốt">
          <ChipList>
            {gio.map((g) => (
              <AlmanacChip key={g.chi} title={g.ly_do.join(" · ")}>
                <strong className="font-semibold">{g.chi}</strong>
                <span className="ml-1 text-muted-foreground">{g.khung}</span>
              </AlmanacChip>
            ))}
          </ChipList>
        </AlmanacRow>
      ) : null}
      {data.huong.length ? (
        <AlmanacRow icon={<Compass />} label="Hướng đi">
          <ChipList>
            {data.huong.map((h) => (
              <AlmanacChip
                key={h.than}
                title={`${h.than}${h.do ? ` · ${h.do}` : ""} — ${h.dung_cho}`}
              >
                <strong className="font-semibold">{h.huong}</strong>
                <span className="ml-1 text-muted-foreground">· {h.dung_cho.split(",")[0]}</span>
              </AlmanacChip>
            ))}
          </ChipList>
        </AlmanacRow>
      ) : null}
    </AlmanacGroup>
  );
}

/** Việc nên / không nên: câu chốt trước, rồi hai dòng chip xanh / đỏ. */
function Thien({ data }: { data: VanKhiLichNgay }) {
  const [open, setOpen] = useState(false);
  const canCu = data.viec_can_cu;
  const warn = data.ngay_ky.length > 0 || !data.nhi_thap_bat_tu.tot;
  return (
    <AlmanacGroup>
      {data.viec_tom_tat ? (
        <p
          className={cn(
            "rounded-xl px-3 py-2 text-[13px] leading-5 text-foreground",
            warn ? "bg-destructive/[0.07]" : "bg-element-wood/[0.08]",
          )}
        >
          {data.viec_tom_tat}
        </p>
      ) : null}
      <AlmanacRow icon={<ThumbsUp className="text-element-wood" />} label="Nên làm">
        <ChipList>
          {data.nen_lam.map((x) => (
            <AlmanacChip key={x} tone="good">
              {x}
            </AlmanacChip>
          ))}
        </ChipList>
      </AlmanacRow>
      <AlmanacRow icon={<ThumbsDown className="text-destructive" />} label="Không nên">
        <ChipList>
          {data.khong_nen.map((x) => (
            <AlmanacChip key={x} tone="bad">
              {x}
            </AlmanacChip>
          ))}
        </ChipList>
      </AlmanacRow>
      {canCu?.length ? (
        <div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[12px] font-semibold text-primary"
          >
            {open ? "Ẩn căn cứ" : "Vì sao lại khuyên như vậy?"}
          </button>
          {open ? (
            <ul className="mt-1.5 space-y-1">
              {canCu.map((c) => (
                <li key={c.ten} className="text-[12px] leading-5 text-foreground/85">
                  <strong className={c.tot ? "text-element-wood" : "text-destructive"}>
                    {c.ten}
                  </strong>
                  : {c.giai_thich}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </AlmanacGroup>
  );
}

/** Sao tốt / xấu theo Ngọc Hạp Thông Thư — mặc định chỉ hiện tên, bấm để xem nghĩa. */
function NgocHap({ data }: { data: VanKhiLichNgay }) {
  const [open, setOpen] = useState(false);
  const nh = data.ngoc_hap;
  if (!nh || (!nh.tot.length && !nh.xau.length)) return null;
  const rows = (list: { name: string; mo_ta: string }[]) =>
    open ? (
      <ul className="space-y-1">
        {list.map((r) => (
          <li key={r.name} className="text-[12px] leading-5 text-foreground/85">
            <strong className="font-semibold">{r.name}</strong>: {r.mo_ta}
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-foreground/85">{list.map((r) => r.name).join(" · ")}</p>
    );
  return (
    <AlmanacGroup>
      {nh.tot.length ? (
        <AlmanacRow icon={<Star className="text-element-wood" />} label="Sao tốt">
          {rows(nh.tot)}
        </AlmanacRow>
      ) : null}
      {nh.xau.length ? (
        <AlmanacRow icon={<Star className="text-destructive" />} label="Sao xấu">
          {rows(nh.xau)}
        </AlmanacRow>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[12px] font-semibold text-primary"
      >
        {open ? "Ẩn giải nghĩa" : "Xem từng sao nghĩa là gì"}
      </button>
    </AlmanacGroup>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] leading-4 text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}

function ScoreInfo({ data }: { data: VanKhiLichNgay }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Điểm hôm nay từ đâu mà có"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="absolute -right-1 top-0 text-muted-foreground transition hover:text-primary"
        >
          <Info className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[60vh] w-[min(18rem,calc(100vw-2rem))] overflow-y-auto p-3"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <p className="mb-1.5 text-xs font-bold text-foreground">Điểm hôm nay từ đâu mà có</p>
        <ExplainBody data={data} />
      </PopoverContent>
    </Popover>
  );
}

/** Bảng cộng/trừ gọn: mỗi yếu tố một dòng, giải thích nằm ở tooltip của dòng. */
function ExplainBody({ data }: { data: VanKhiLichNgay }) {
  const groups = (["nen", "ngay", "tuoi", "viec"] as const)
    .map((g) => ({ g, rows: data.breakdown.filter((b) => b.group === g) }))
    .filter((x) => x.rows.length);
  const raw = data.breakdown.reduce((s, b) => s + b.delta, 0);
  return (
    <div className="space-y-1.5">
      {groups.map(({ g, rows }) => (
        <div key={g}>
          <p className="text-[10px] font-semibold text-muted-foreground">{GROUP_LABEL[g]}</p>
          <ul>
            {rows.map((b, i) => (
              <li
                key={`${b.label}-${i}`}
                title={b.detail || undefined}
                className="flex items-center gap-2 py-0.5 text-[12px] text-foreground/90"
              >
                <span className="min-w-0 flex-1 truncate">{b.label}</span>
                <span
                  className={cn(
                    "shrink-0 font-bold tabular-nums",
                    g === "nen"
                      ? "text-foreground/70"
                      : b.delta > 0
                        ? "text-element-wood"
                        : b.delta < 0
                          ? "text-destructive"
                          : "text-muted-foreground",
                  )}
                >
                  {g === "nen" ? b.delta : b.delta > 0 ? `+${b.delta}` : b.delta}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="flex items-center justify-between border-t border-border/60 pt-1.5 text-[12px] font-bold text-foreground">
        <span>Tổng{raw !== data.score ? " (giới hạn 0–100)" : ""}</span>
        <span className="tabular-nums">{data.score}</span>
      </p>
      {data.chi_so.nam !== null ? (
        <p className="border-t border-border/60 pt-1.5 text-[11px] text-muted-foreground">
          Theo tuổi bạn: tháng âm <strong className="text-foreground">{data.chi_so.thang}</strong> ·
          năm {data.nen_van_nam?.chi_nam ?? ""}{" "}
          <strong className="text-foreground">{data.chi_so.nam}</strong>
          {data.nen_van_nam?.ghi_chu.length ? ` — ${data.nen_van_nam.ghi_chu.join("; ")}` : ""}
        </p>
      ) : null}
    </div>
  );
}

function More({ data }: { data: VanKhiLichNgay }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border-t border-border/60 pt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between py-2 text-left"
      >
        <span className="text-[12px] font-bold text-foreground">Thông tin thêm trong ngày</span>
        <ChevronDown
          className={cn("size-4 text-muted-foreground transition", open && "rotate-180")}
        />
      </button>
      {open ? (
        <div className="space-y-3 pb-1 text-[12px]">
          {data.nap_am ? (
            <p>
              <strong>Ngũ hành ngày:</strong> {data.nap_am.name} (hành {data.nap_am.element}) · tiết{" "}
              {data.tiet_khi}
            </p>
          ) : null}
          {data.tuoi_xung_khac.length ? (
            <div>
              <p>
                <strong>Tuổi xung khắc với ngày:</strong> {data.tuoi_xung_khac.join(", ")}
              </p>
              <p className="text-[11px] text-muted-foreground">
                Chi tuổi xung chi ngày, lại thêm nạp âm khắc nhau — tuổi này nên tránh việc lớn hôm
                nay.
              </p>
            </div>
          ) : null}
          <div>
            <p className="font-semibold">Giờ hoàng đạo</p>
            <p className="text-muted-foreground">
              {data.gio_hoang_dao.map((g) => `${g.gio_chi} (${g.khung_gio})`).join(" · ")}
            </p>
          </div>
          <div>
            <p className="font-semibold">Giờ xuất hành Lý Thuần Phong</p>
            <ul className="mt-1.5 space-y-2">
              {data.ly_thuan_phong.map((l) => (
                <li key={l.name}>
                  <p
                    className={cn(
                      "font-semibold",
                      l.tot ? "text-element-wood" : "text-destructive",
                    )}
                  >
                    {l.name}: {l.gio.map((g) => `${g.chi} (${g.khung})`).join(" & ")}
                  </p>
                  <p className="text-[12px] text-muted-foreground">{l.y_nghia}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
